import React, { useState, useEffect, useRef } from "react";
import {
    Drawer, Box, Typography, Divider, IconButton, TextField, Button,
    CircularProgress, Chip, Tooltip, Alert, ToggleButtonGroup, ToggleButton,
    Avatar, Link, useTheme, useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import MessageBubble from "./MessageBubble";
import TemplateMessageDialog from "./TemplateMessageDialog";
import { SIN_ZOOM_IPHONE } from "./sinZoomIphone";
import { claveDia, etiquetaDia } from "./fechas";
import whatsappServices from "./whatsappServices";
import moment from "moment";

const DRAWER_WIDTH = 480;

export default function ConversationDrawer({ open, conversation, onClose, onConversationUpdated, onArchivedChange, onConversationClosed, refreshToken }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [detail, setDetail]       = useState(null);
    const [loading, setLoading]     = useState(false);
    const [sending, setSending]     = useState(false);
    const [newMessage, setNewMessage] = useState("");
    const [modeLoading, setModeLoading] = useState(false);
    const [error, setError]         = useState(null);
    const [templateOpen, setTemplateOpen] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [closing, setClosing] = useState(false);
    const messagesEndRef             = useRef(null);
    const messagesContainerRef       = useRef(null);

    const scrollToBottom = () => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    };

    // Cargar detalle completo (con mensajes) cuando cambia la conversación
    useEffect(() => {
        if (!open || !conversation) return;
        let cancelled = false;
        setDetail(null);
        setError(null);
        setLoading(true);
        whatsappServices.getConversation(conversation.id)
            .then(data => { if (!cancelled) setDetail(data); })
            .catch(() => { if (!cancelled) setError("Error al cargar la conversación"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, conversation?.id]);

    // Con cada aviso de mensaje nuevo se recarga por detrás, sin vaciar lo que se está
    // leyendo. Antes cada aviso (varios por mensaje) borraba la conversación, mostraba el
    // spinner y la volvía a pintar.
    useEffect(() => {
        if (!open || !conversation || !refreshToken) return;
        let cancelled = false;
        whatsappServices.getConversation(conversation.id)
            .then(data => { if (!cancelled) setDetail(data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-scroll al final cuando llegan mensajes, no con cada recarga: si no, saltaba al
    // final mientras se leía más arriba.
    // setTimeout 0 espera que React termine de pintar los MessageBubble en el DOM
    useEffect(() => {
        if (detail?.whatsapp_messages?.length) {
            setTimeout(scrollToBottom, 0);
        }
    }, [detail?.whatsapp_messages?.length]);

    const conv = detail || conversation;
    if (!conv) return null;

    const contact  = conv.contact || {};
    const messages = detail?.whatsapp_messages || [];
    const leadData = conv.lead_data || {};
    const mode     = conv.mode || "bot";
    const status   = conv.status || "open";

    const handleSend = async () => {
        if (!newMessage.trim() || sending) return;
        setSending(true);
        setError(null);
        try {
            await whatsappServices.sendMessage(conv.id, newMessage.trim());
            setNewMessage("");
            // Recargar mensajes
            const updated = await whatsappServices.getConversation(conv.id);
            setDetail(updated);
            onConversationUpdated?.();
        } catch (e) {
            // El servidor explica por que no se pudo (por ejemplo, la ventana de 24 horas).
            setError(e?.response?.data?.error || "Error al enviar el mensaje");
        } finally {
            setSending(false);
        }
    };

    const handleModeChange = async (_, newMode) => {
        if (!newMode || newMode === mode || modeLoading) return;
        setModeLoading(true);
        setError(null);
        try {
            await whatsappServices.updateMode(conv.id, { mode: newMode });
            const updated = await whatsappServices.getConversation(conv.id);
            setDetail(updated);
            onConversationUpdated?.();
        } catch (e) {
            setError("Error al cambiar el modo");
        } finally {
            setModeLoading(false);
        }
    };

    // Archiva al contacto: sale del inbox y deja de avisar. El bot le sigue contestando.
    const handleArchive = async () => {
        if (archiving) return;
        const archivar = !contact.archived;
        if (archivar && !window.confirm(
            "¿Archivar este contacto? Sale del inbox y deja de avisar sus mensajes. Se puede ver y desarchivar en \"Archivados\"."
        )) return;
        setArchiving(true);
        setError(null);
        try {
            await whatsappServices.setArchived(conv.id, archivar);
            onArchivedChange?.();
        } catch (e) {
            setError(archivar ? "Error al archivar" : "Error al desarchivar");
        } finally {
            setArchiving(false);
        }
    };

    // Cierra la conversación cuando ya no hay nada más que hacer (por ejemplo, la reunión ya fue).
    // Si HubSpot falla no se cierra y el servidor explica por qué.
    const handleCloseConversation = async () => {
        if (closing) return;
        if (!window.confirm(
            "¿Cerrar esta conversación? Se pasa a HubSpot si no está, vuelve al bot y sale del inbox (Cami deja de hacerle seguimiento). Si vuelve a escribir, reaparece."
        )) return;
        setClosing(true);
        setError(null);
        try {
            const result = await whatsappServices.closeConversation(conv.id);
            onConversationClosed?.(conv, result);
        } catch (e) {
            setError(e?.response?.data?.error || "Error al cerrar la conversación");
        } finally {
            setClosing(false);
        }
    };

    const handleTemplateSent = (updated) => {
        setTemplateOpen(false);
        setDetail(updated);
        onConversationUpdated?.();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const name      = contact.name || contact.phone || contact.wa_id || "Desconocido";
    const initials  = name.slice(0, 2).toUpperCase();
    const isAiFlow  = leadData.sales_flow;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            variant={isMobile ? "temporary" : "persistent"}
            PaperProps={{
                sx: {
                    ...SIN_ZOOM_IPHONE,
                    width: isMobile ? "100%" : DRAWER_WIDTH,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
                },
            }}
        >
            {/* ── HEADER ─────────────────────────────────────── */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1, bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: isAiFlow ? "#7b1fa2" : "#1565c0", width: 38, height: 38, fontSize: 14 }}>
                        {initials}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 14 }}>
                                {name}
                            </Typography>
                            {isAiFlow && (
                                <Chip
                                    icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 12 }} />}
                                    label="Lead IA"
                                    size="small"
                                    sx={{ height: 18, fontSize: 10, bgcolor: "#ede7f6", color: "#7b1fa2" }}
                                />
                            )}
                        </Box>
                        <Typography variant="caption" sx={{ color: "#888", fontSize: 11 }}>
                            {contact.phone || contact.wa_id}
                        </Typography>
                    </Box>
                    {!contact.archived && (
                        <Tooltip title="Cerrar conversación (pasa a HubSpot si no está)">
                            <span>
                                <IconButton size="small" onClick={handleCloseConversation} disabled={closing}>
                                    {closing
                                        ? <CircularProgress size={16} />
                                        : <TaskAltOutlinedIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title={contact.archived ? "Desarchivar contacto" : "Archivar contacto"}>
                        <span>
                            <IconButton size="small" onClick={handleArchive} disabled={archiving}>
                                {contact.archived
                                    ? <UnarchiveOutlinedIcon fontSize="small" />
                                    : <ArchiveOutlinedIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Badges de status + freshdesk */}
                <Box sx={{ display: "flex", gap: 1, mt: 0.8, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                        label={status === "open" ? "Abierta" : "Cerrada"}
                        size="small"
                        sx={{
                            height: 18, fontSize: 10,
                            bgcolor: status === "open" ? "#e8f5e9" : "#f5f5f5",
                            color: status === "open" ? "#2e7d32" : "#757575",
                        }}
                    />
                    {contact.archived && (
                        <Chip label="Archivado" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#eceff1", color: "#546e7a" }} />
                    )}
                    {conv.freshdesk_ticket_id && (
                        <Chip
                            label={`Ticket #${conv.freshdesk_ticket_id}`}
                            size="small"
                            component={conv.freshdesk_ticket_url ? "a" : "div"}
                            href={conv.freshdesk_ticket_url}
                            target="_blank"
                            clickable={!!conv.freshdesk_ticket_url}
                            deleteIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                            sx={{
                                height: 18, fontSize: 10,
                                bgcolor: "#fff3e0", color: "#e65100",
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* ── LEAD DATA (si tiene datos de IA) ───────────── */}
            {isAiFlow && Object.values(leadData).some(v => v && v !== true) && (
                <Box sx={{ px: 2, py: 1, bgcolor: "#f9f4ff", borderBottom: "1px solid #e0e0e0" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#7b1fa2", textTransform: "uppercase", fontSize: 10 }}>
                        Datos del lead
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 0.5 }}>
                        {leadData.name && <LeadTag label="Nombre" value={leadData.name} />}
                        {leadData.company && <LeadTag label="Empresa" value={leadData.company} />}
                        {leadData.email && <LeadTag label="Email" value={leadData.email} />}
                        {leadData.erp && <LeadTag label="ERP" value={leadData.erp} />}
                        {leadData.deliveries_per_day && <LeadTag label="Entregas/día" value={leadData.deliveries_per_day} />}
                        {leadData.channels && <LeadTag label="Canales" value={leadData.channels} />}
                        {leadData.sales_stage && <LeadTag label="Etapa" value={leadData.sales_stage} />}
                        {leadData.last_ai_score != null && (
                            <LeadTag label="Score" value={`${(leadData.last_ai_score * 100).toFixed(0)}%`} />
                        )}
                    </Box>
                </Box>
            )}

            {/* ── MENSAJES ────────────────────────────────────── */}
            <Box
                ref={messagesContainerRef}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    py: 1.5,
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && messages.length === 0 && (
                    <Typography variant="body2" sx={{ textAlign: "center", color: "#bbb", mt: 4 }}>
                        Sin mensajes
                    </Typography>
                )}

                {/* Un separador cada vez que cambia el día, como en WhatsApp: la burbuja solo dice la hora */}
                {!loading && messages.map((msg, i) => {
                    const nuevoDia = i === 0 || claveDia(msg.created_at) !== claveDia(messages[i - 1].created_at);
                    return (
                        <React.Fragment key={msg.id}>
                            {nuevoDia && (
                                <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ bgcolor: "#f0f0f0", color: "#54656f", px: 1.5, py: 0.3, borderRadius: 2, fontSize: 11, fontWeight: 600 }}
                                    >
                                        {etiquetaDia(msg.created_at)}
                                    </Typography>
                                </Box>
                            )}
                            <MessageBubble message={msg} />
                        </React.Fragment>
                    );
                })}

                <div ref={messagesEndRef} />
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mb: 1, fontSize: 12 }}>
                    {error}
                </Alert>
            )}

            {/* ── FOOTER: modo + input ────────────────────────── */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: "#fafafa", borderTop: "1px solid #e0e0e0" }}>
                {/* Toggle bot/human */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={handleModeChange}
                        size="small"
                        disabled={modeLoading}
                    >
                        <ToggleButton
                            value="bot"
                            sx={{
                                fontSize: 11, px: 1.5, py: 0.4,
                                "&.Mui-selected": { bgcolor: "#e3f2fd", color: "#1565c0" },
                            }}
                        >
                            <SmartToyOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            Bot
                        </ToggleButton>
                        <ToggleButton
                            value="human"
                            sx={{
                                fontSize: 11, px: 1.5, py: 0.4,
                                "&.Mui-selected": { bgcolor: "#e8f5e9", color: "#2e7d32" },
                            }}
                        >
                            <PersonOutlineOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            Humano
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {modeLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}

                    <Typography variant="caption" sx={{ color: "#999", fontSize: 10 }}>
                        {conv.last_inbound_at ? `Último: ${moment(conv.last_inbound_at).fromNow()}` : ""}
                    </Typography>
                </Box>

                {/* Input de respuesta. Pasadas 24 horas desde el último mensaje del cliente, WhatsApp
                    no entrega texto libre: se avisa antes de escribir, en vez de dejar una
                    respuesta que parece enviada y nunca llega. */}
                {mode === "human" && conv.window_open === false ? (
                    <Alert
                        severity="warning"
                        sx={{ fontSize: 12, alignItems: "center" }}
                        action={
                            <Button color="inherit" size="small" onClick={() => setTemplateOpen(true)} sx={{ whiteSpace: "nowrap" }}>
                                Enviar plantilla
                            </Button>
                        }
                    >
                        {conv.last_inbound_at
                            ? "Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp no deja escribirle texto libre hasta que vuelva a escribir. Solo se le puede mandar una plantilla aprobada."
                            : "Todavía no responde. Cuando lo haga podrás escribirle libremente; mientras tanto, solo con una plantilla aprobada."}
                    </Alert>
                ) : mode === "human" ? (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                        <TextField
                            multiline
                            maxRows={4}
                            fullWidth
                            size="small"
                            placeholder="Escribe un mensaje… (Enter para enviar)"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={sending}
                            sx={{ fontSize: 13, "& .MuiInputBase-input": { fontSize: 13 } }}
                        />
                        <Tooltip title="Enviar (Enter)">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={handleSend}
                                    disabled={!newMessage.trim() || sending}
                                    sx={{ mb: 0.2 }}
                                >
                                    {sending ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ) : conv.window_open === false ? (
                    // En modo Bot con la ventana cerrada, cambiar a Humano no sirve de nada: solo se
                    // le puede escribir con plantilla. Mandarla pasa la conversación a Humano.
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography variant="caption" sx={{ color: "#999" }}>
                            Pasaron más de 24 horas: solo se le puede escribir con una plantilla.
                        </Typography>
                        <Button size="small" variant="outlined" onClick={() => setTemplateOpen(true)} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                            Enviar plantilla
                        </Button>
                    </Box>
                ) : (
                    <Typography variant="caption" sx={{ color: "#999", textAlign: "center", py: 0.5 }}>
                        Cambia a modo Humano para responder
                    </Typography>
                )}
            </Box>
            <TemplateMessageDialog
                open={templateOpen}
                conversation={conv}
                onClose={() => setTemplateOpen(false)}
                onSent={handleTemplateSent}
            />
        </Drawer>
    );
}

// Pequeño componente de etiqueta para datos del lead
function LeadTag({ label, value }) {
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, bgcolor: "#ede7f6", borderRadius: 1, px: 0.8, py: 0.2 }}>
            <Typography sx={{ fontSize: 10, color: "#9e9e9e", fontWeight: 600 }}>{label}:</Typography>
            <Typography sx={{ fontSize: 10, color: "#4a148c", fontWeight: 600 }}>{value}</Typography>
        </Box>
    );
}
